import React, { useMemo, useState, useEffect } from "react";
import {
  Building2, FileText, AlertTriangle, Network, Upload, Download, FileImage, FileStack,
  Layers, Users2, ExternalLink, Search, ArrowRight, Percent, Sun, Moon, ShieldCheck, Palette,
  CircleDot, ClipboardList, TrendingUp, X, RefreshCw, ScrollText, Archive, Save, Clock,
} from "lucide-react";
import { COMPANIES, COMPANY_ORDER, PERSONS, PERSON_ORDER, STEPS, ddmmyyyy, formatRupiah } from "./data";
import { activeVersion, companyFlagLevel, effective, Flag, EffCache } from "./model";
import { toPercentString, greaterThanPercent, isZero } from "./rational";
import { fileUrl, activityApi, ActivityItem, settingsApi, archiveApi, ArchiveItem } from "./api";
import type { PageId } from "./Sidebar";
import type { UBORow } from "./exportChart";
import { exportActivityLogPDF } from "./exportChart";

export interface Ctx {
  groupName: string;
  effStep: number;
  cache: EffCache;
  flags: Flag[];
  counts: { red: number; yellow: number };
  canEdit: boolean;
  user: { name?: string; email?: string; role?: string } | null;
  theme: "light" | "dark";
  onToggleTheme: () => void;
  companyCat: Record<string, string>;
  docExtra: Record<string, Record<string, { note?: string; url?: string }>>;
  focusCompany: (cid: string) => void;
  onUploadForCompany?: (cid: string) => void;
  onNavigate: (p: PageId) => void;
  onOpenExtract: () => void;
  openExport: (kind: "png" | "pdf") => void;
  exportAllSteps: () => void;
  exportBatchPerPT: () => void;
  onExportUBO: () => void;
  onExportClientSummary: () => void;
  exporting: boolean;
  onOpenUsers: () => void;
  uboReport: UBORow[];
  ptWithMissing: number;
  docCompleteness: { cid: string; name: string; missing: { label: string }[] }[];
  groupList: { id: string; name: string }[];
  currentGroupId: string;
  onChangeGroup: (id: string) => void;
}

function docHref(d: { url: string | null; storagePath?: string }): string | null {
  if (d.url) return d.url;
  if (d.storagePath) return fileUrl(d.storagePath);
  return null;
}

function shareholderCount(cid: string, step: number): number {
  const v = activeVersion(cid, step);
  if (!v) return 0;
  return new Set(v.holders.map((h) => h[0])).size;
}

function latestChange(cid: string, step: number): { t: string; date: string } | null {
  const c = COMPANIES[cid];
  const hist = c.history.filter((h) => h.step <= step).sort((a, b) => b.step - a.step);
  const h = hist[0];
  if (!h) return null;
  const st = STEPS[h.step];
  return { t: h.t, date: st ? ddmmyyyy(st.date) : "" };
}

/* ================= Dashboard ================= */
export function DashboardPage(ctx: Ctx) {
  const companies = COMPANY_ORDER.length;
  const docTotal = COMPANY_ORDER.reduce((n, cid) => n + COMPANIES[cid].documents.length, 0);
  const issues = ctx.counts.red + ctx.counts.yellow;
  const recent = STEPS.map((s, i) => ({ ...s, i })).slice().reverse().slice(0, 6);
  const completePct = companies === 0 ? 0 : Math.round(((companies - ctx.ptWithMissing) / companies) * 100);

  if (companies === 0) return <EmptyGroup onExtract={ctx.onOpenExtract} canEdit={ctx.canEdit} />;

  return (
    <div data-testid="page-dashboard">
      <div className="page-head page-head-row">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">Ringkasan grup <b>{ctx.groupName}</b></div>
        </div>
        {ctx.canEdit && (
          <button className="d-btn-primary" onClick={ctx.onOpenExtract} data-testid="dash-extract">
            <Upload size={18} /><span className="pt"><b>Ekstraksi AI</b><small>Upload dokumen legalitas</small></span>
          </button>
        )}
      </div>

      <div className="stat-grid">
        <div className="card stat-card" data-testid="stat-companies">
          <div className="st-ic blue"><Building2 size={20} /></div>
          <div className="st-val">{companies}</div><div className="st-lbl">Perusahaan</div>
        </div>
        <div className="card stat-card" data-testid="stat-docs">
          <div className="st-ic green"><FileText size={20} /></div>
          <div className="st-val">{docTotal}</div><div className="st-lbl">Dokumen legal</div>
        </div>
        <div className="card stat-card" data-testid="stat-issues">
          <div className="st-ic amber"><AlertTriangle size={20} /></div>
          <div className="st-val">{issues}</div><div className="st-lbl">Data issues</div>
        </div>
        <div className="card stat-card" data-testid="stat-complete">
          <div className="st-ic green"><ShieldCheck size={20} /></div>
          <div className="st-val">{completePct}%</div><div className="st-lbl">Kelengkapan dokumen</div>
        </div>
      </div>

      <div className="dash-cols">
        <div className="card panel-block">
          <div className="panel-block-head">
            <span className="panel-block-title">Perubahan terbaru</span>
            <button className="tbl-link" onClick={() => ctx.onNavigate("struktur")} data-testid="dash-goto-timeline">Lihat timeline <ArrowRight size={13} style={{ verticalAlign: "-2px" }} /></button>
          </div>
          {recent.map((s) => (
            <div className="mini-timeline-item" key={s.i}>
              <span className="mtl-dot" />
              <div>
                <div className="mtl-date">{ddmmyyyy(s.date)}</div>
                <div className="mtl-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="card panel-block">
          <div className="panel-block-head"><span className="panel-block-title">Status kelengkapan</span></div>
          {ctx.docCompleteness.map((r) => (
            <div className="settings-row" key={r.cid} style={{ padding: "10px 0" }}>
              <button className="tbl-link" onClick={() => ctx.focusCompany(r.cid)} data-testid={`dash-pt-${r.cid}`}>{r.name}</button>
              {r.missing.length === 0
                ? <span className="pill ok">Lengkap</span>
                : <span className="pill warn" data-testid={`dash-pt-missing-${r.cid}`}>{r.missing.length} kurang</span>}
            </div>
          ))}
          <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
            <button className="d-btn" onClick={() => ctx.onNavigate("perusahaan")} data-testid="dash-goto-companies"><Building2 size={15} /> Perusahaan</button>
            <button className="d-btn" onClick={() => ctx.onNavigate("issues")} data-testid="dash-goto-issues"><AlertTriangle size={15} /> Data Issues</button>
            <button className="d-btn" onClick={() => ctx.onNavigate("laporan")} data-testid="dash-goto-reports"><TrendingUp size={15} /> Laporan</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Perusahaan ================= */
export function CompaniesPage(ctx: Ctx) {
  const [q, setQ] = useState("");
  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return COMPANY_ORDER
      .map((cid) => {
        const c = COMPANIES[cid];
        const level = companyFlagLevel(ctx.flags, cid);
        const missing = ctx.docCompleteness.find((r) => r.cid === cid)?.missing.length ?? 0;
        return { cid, c, level, sh: shareholderCount(cid, ctx.effStep), docs: c.documents.length, missing, last: latestChange(cid, ctx.effStep) };
      })
      .filter((r) => !ql || r.c.name.toLowerCase().includes(ql) || r.c.kedudukan.toLowerCase().includes(ql));
  }, [q, ctx.flags, ctx.effStep, ctx.docCompleteness]);

  if (COMPANY_ORDER.length === 0) return <EmptyGroup onExtract={ctx.onOpenExtract} canEdit={ctx.canEdit} />;

  return (
    <div data-testid="page-perusahaan">
      <div className="page-head page-head-row">
        <div>
          <div className="page-title">Perusahaan</div>
          <div className="page-sub">{COMPANY_ORDER.length} perusahaan dalam grup <b>{ctx.groupName}</b></div>
        </div>
        <div className="toolbar-search"><Search className="ts-ic" size={15} /><input placeholder="Cari perusahaan..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="companies-search" /></div>
      </div>
      <div className="card tbl-wrap">
        <table className="data-table">
          <thead><tr><th>Perusahaan</th><th>Status</th><th className="num">Pemegang Saham</th><th className="num">Dokumen</th><th>Kelengkapan</th><th>Perubahan Terakhir</th><th></th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cid} data-testid={`company-row-${r.cid}`}>
                <td><button className="tbl-link cell-strong" onClick={() => ctx.focusCompany(r.cid)}>{r.c.name}</button><div className="cell-sub">{r.c.kedudukan}</div></td>
                <td>{r.level === "red" ? <span className="pill bad"><span className="qdot" style={{ background: "var(--danger2)" }} /> Perhatian</span> : r.level === "yellow" ? <span className="pill warn"><span className="qdot" style={{ background: "var(--amber)" }} /> Tinjau</span> : <span className="pill ok">OK</span>}</td>
                <td className="num">{r.sh}</td>
                <td className="num">{r.docs}</td>
                <td>{r.missing === 0 ? <span className="pill ok">Lengkap</span> : <span className="pill warn">{r.missing} kurang</span>}</td>
                <td>{r.last ? <><span className="cell-strong">{r.last.t}</span><div className="cell-sub">{r.last.date}</div></> : <span className="cell-sub">—</span>}</td>
                <td><button className="d-btn ghost" onClick={() => ctx.focusCompany(r.cid)} data-testid={`company-view-${r.cid}`}><Network size={14} /> Lihat di Struktur</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Dokumen ================= */
export function DocumentsPage(ctx: Ctx) {
  const [q, setQ] = useState("");
  const [fJenis, setFJenis] = useState("all");
  const [fPT, setFPT] = useState("all");
  const [fTahun, setFTahun] = useState("all");

  const allRows = useMemo(() => {
    const out: { key: string; cid: string; company: string; label: string; kind: string; date: string; year: string; href: string | null; source: string }[] = [];
    for (const cid of COMPANY_ORDER) {
      const c = COMPANIES[cid];
      c.documents.forEach((d, i) => {
        out.push({
          key: `${cid}-${i}`, cid, company: c.name, label: d.label,
          kind: d.kind, date: d.date ? ddmmyyyy(d.date) : "—", year: d.date ? d.date.slice(0, 4) : "",
          href: docHref(d), source: d.storagePath ? "ai" : d.url ? "arsip" : "none",
        });
      });
    }
    return out;
  }, [ctx.currentGroupId]);

  const jenisOptions = useMemo(() => Array.from(new Set(allRows.map((r) => r.kind))), [allRows]);
  const yearOptions = useMemo(() => Array.from(new Set(allRows.map((r) => r.year).filter(Boolean))).sort((a, b) => b.localeCompare(a)), [allRows]);

  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return allRows.filter((r) =>
      (!ql || r.label.toLowerCase().includes(ql) || r.company.toLowerCase().includes(ql)) &&
      (fJenis === "all" || r.kind === fJenis) &&
      (fPT === "all" || r.cid === fPT) &&
      (fTahun === "all" || r.year === fTahun)
    );
  }, [allRows, q, fJenis, fPT, fTahun]);

  const kindLabel = (k: string) => k === "pendirian" ? "Akta Pendirian" : k === "perubahan" ? "Akta Perubahan" : k === "nib" ? "NIB" : k === "npwp" ? "NPWP" : k;
  const hasFilter = fJenis !== "all" || fPT !== "all" || fTahun !== "all" || q.trim() !== "";
  const resetFilters = () => { setQ(""); setFJenis("all"); setFPT("all"); setFTahun("all"); };

  if (COMPANY_ORDER.length === 0) return <EmptyGroup onExtract={ctx.onOpenExtract} canEdit={ctx.canEdit} />;

  return (
    <div data-testid="page-dokumen">
      <div className="page-head page-head-row">
        <div>
          <div className="page-title">Dokumen</div>
          <div className="page-sub">Seluruh dokumen legal grup <b>{ctx.groupName}</b>{ctx.ptWithMissing > 0 ? ` · ${ctx.ptWithMissing} PT dokumennya belum lengkap` : ""}</div>
        </div>
        {ctx.canEdit && <button className="d-btn-primary" onClick={ctx.onOpenExtract} data-testid="documents-extract"><Upload size={17} /><span className="pt"><b>Ekstraksi AI</b><small>Upload dokumen</small></span></button>}
      </div>

      <div className="filter-bar" data-testid="documents-filters">
        <div className="toolbar-search" style={{ maxWidth: 240 }}><Search className="ts-ic" size={15} /><input placeholder="Cari dokumen..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="documents-search" /></div>
        <label className="filter-field">
          <span>Grup Klien</span>
          <select className="filter-select" value={ctx.currentGroupId} onChange={(e) => ctx.onChangeGroup(e.target.value)} data-testid="filter-group">
            {ctx.groupList.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Jenis</span>
          <select className="filter-select" value={fJenis} onChange={(e) => setFJenis(e.target.value)} data-testid="filter-jenis">
            <option value="all">Semua jenis</option>
            {jenisOptions.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Perusahaan</span>
          <select className="filter-select" value={fPT} onChange={(e) => setFPT(e.target.value)} data-testid="filter-perusahaan">
            <option value="all">Semua perusahaan</option>
            {COMPANY_ORDER.map((cid) => <option key={cid} value={cid}>{COMPANIES[cid].name}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Tahun</span>
          <select className="filter-select" value={fTahun} onChange={(e) => setFTahun(e.target.value)} data-testid="filter-tahun">
            <option value="all">Semua tahun</option>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        {hasFilter && <button className="d-btn ghost" onClick={resetFilters} data-testid="filter-reset"><X size={14} /> Reset</button>}
      </div>

      <div className="card tbl-wrap">
        <table className="data-table">
          <thead><tr><th>Dokumen</th><th>Perusahaan</th><th>Jenis</th><th>Tanggal</th><th>Ekstraksi AI</th><th>Status Review</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7}><div className="cell-sub" style={{ padding: "22px 4px", textAlign: "center" }} data-testid="documents-empty">Tidak ada dokumen yang cocok dengan filter.</div></td></tr>
            ) : rows.map((r) => (
              <tr key={r.key} data-testid={`doc-row-${r.key}`}>
                <td className="cell-strong">{r.label}</td>
                <td>{r.company}</td>
                <td><span className="pill neutral">{kindLabel(r.kind)}</span></td>
                <td>{r.date}</td>
                <td>{r.source === "ai" ? <span className="pill info">Ekstraksi AI</span> : r.source === "arsip" ? <span className="pill neutral">Arsip</span> : <span className="pill warn">Belum ada</span>}</td>
                <td>{r.source === "none" ? <span className="cell-sub">—</span> : r.source === "ai" ? <span className="pill warn">Perlu tinjau</span> : <span className="pill ok">Tersedia</span>}</td>
                <td>{r.href ? <a className="d-btn ghost" href={r.href} target="_blank" rel="noreferrer" data-testid={`doc-open-${r.key}`}><ExternalLink size={14} /> Buka</a> : <span className="cell-sub">Belum di-upload</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ================= Data Issues ================= */
export function IssuesPage(ctx: Ctx) {
  const sevIc = (s: string) => s === "info" ? <CircleDot size={17} /> : <AlertTriangle size={17} />;
  const sevName = (s: string) => s === "red" ? "Kritis" : s === "yellow" ? "Perlu tinjau" : "Catatan";

  if (COMPANY_ORDER.length === 0) return <EmptyGroup onExtract={ctx.onOpenExtract} canEdit={ctx.canEdit} />;

  const issues = ctx.flags.filter((f) => f.sev === "red" || f.sev === "yellow");
  const notes = ctx.flags.filter((f) => f.sev === "info");

  const IssueCard = (f: Flag, i: number, kind: "issue" | "note") => {
    const c = COMPANIES[f.companyId];
    return (
      <div className="card issue-card" key={`${kind}-${i}`} data-testid={`${kind}-${i}`}>
        <div className={"issue-sev " + f.sev}>{sevIc(f.sev)}</div>
        <div className="issue-main">
          <div className="issue-title">
            <span>{c?.name ?? f.companyId}</span>
            <span className={"pill " + (f.sev === "red" ? "bad" : f.sev === "yellow" ? "warn" : "info")}>{sevName(f.sev)}</span>
            <span className="issue-code">{f.code}</span>
          </div>
          <div className="issue-msg">{f.message}</div>
          {f.source && <div className="issue-src">Sumber: {f.source}</div>}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button className="d-btn ghost" onClick={() => ctx.focusCompany(f.companyId)} data-testid={`${kind}-view-${i}`}><Network size={14} /> Lihat</button>
          {ctx.canEdit && ctx.onUploadForCompany && /DOK/i.test(f.code) && (
            <button className="d-btn" onClick={() => ctx.onUploadForCompany!(f.companyId)} data-testid={`${kind}-upload-${i}`}><Upload size={14} /> Susulkan dokumen</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="page-issues">
      <div className="page-head">
        <div className="page-title">Data Issues</div>
        <div className="page-sub">
          {issues.length === 0 ? <>Tidak ada isu yang perlu ditinjau pada grup <b>{ctx.groupName}</b>.</> : <>{ctx.counts.red} kritis, {ctx.counts.yellow} perlu tinjau pada grup <b>{ctx.groupName}</b></>}
        </div>
        <div className="page-sub" style={{ marginTop: 2, fontSize: 12.5, color: "var(--tx-3)" }}>Menilai <b>konsistensi &amp; kualitas data</b> hasil ekstraksi/analisis. Untuk kelengkapan berkas legal (Akta/NIB/NPWP), lihat menu <b>Dokumen</b>.</div>
      </div>

      {issues.length === 0 ? (
        <div className="card"><div className="empty-state"><div className="es-ic"><ShieldCheck size={44} /></div><div className="es-title">Tidak ada isu</div><div className="es-sub">Tidak ada data kritis atau yang perlu ditinjau pada struktur langkah aktif. Hal-hal yang umum/wajar ditampilkan sebagai catatan di bawah.</div></div></div>
      ) : (
        issues.map((f, i) => IssueCard(f, i, "issue"))
      )}

      {notes.length > 0 && (
        <>
          <div className="page-head" style={{ marginTop: 28, marginBottom: 14 }}>
            <div className="panel-block-title" style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CircleDot size={16} color="var(--tx-3)" /> Catatan ({notes.length})
            </div>
            <div className="page-sub" style={{ marginTop: 4 }}>Hal-hal berikut umum terjadi dan biasanya tidak memerlukan tindakan — hanya untuk perhatian saat peninjauan.</div>
          </div>
          {notes.map((f, i) => IssueCard(f, i, "note"))}
        </>
      )}
    </div>
  );
}

/* ================= Laporan ================= */
export function ReportsPage(ctx: Ctx) {
  if (COMPANY_ORDER.length === 0) return <EmptyGroup onExtract={ctx.onOpenExtract} canEdit={ctx.canEdit} />;
  const tiles = [
    { id: "png", icon: FileImage, title: "Download PNG", sub: "Ekspor struktur langkah aktif ke gambar PNG.", onClick: () => ctx.openExport("png") },
    { id: "pdf", icon: FileText, title: "PDF Struktur Saat Ini", sub: "Ekspor struktur langkah aktif ke PDF.", onClick: () => ctx.openExport("pdf") },
    { id: "pdf-all", icon: FileStack, title: "PDF Semua Langkah", sub: "Tiap langkah perubahan menjadi satu halaman PDF.", onClick: ctx.exportAllSteps },
    { id: "pdf-per-pt", icon: Layers, title: "PDF per PT", sub: "Tiap PT menjadi satu halaman berisi rantai kepemilikannya.", onClick: ctx.exportBatchPerPT },
    { id: "ubo", icon: Users2, title: "Ringkasan UBO", sub: "Ringkasan Ultimate Beneficial Owner (>25% efektif).", onClick: ctx.onExportUBO },
    { id: "client", icon: ClipboardList, title: "Ringkasan Klien", sub: "PDF 1 halaman bermerk STRUKTURKU untuk dikirim ke klien.", onClick: ctx.onExportClientSummary },
  ];
  return (
    <div data-testid="page-laporan">
      <div className="page-head">
        <div className="page-title">Analisis &amp; Laporan</div>
        <div className="page-sub">UBO, kepemilikan efektif, dan ekspor untuk grup <b>{ctx.groupName}</b></div>
      </div>
      <div className="report-grid">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div className="card report-tile" key={t.id} onClick={() => !ctx.exporting && t.onClick()} data-testid={`report-${t.id}`}>
              <div className="rt-ic"><Icon size={20} /></div>
              <div className="rt-title">{t.title}</div>
              <div className="rt-sub">{t.sub}</div>
              <div style={{ marginTop: 4 }}><span className="d-btn ghost" style={{ pointerEvents: "none" }}><Download size={14} /> {ctx.exporting ? "Memproses…" : "Ekspor"}</span></div>
            </div>
          );
        })}
      </div>

      <div className="card panel-block">
        <div className="panel-block-head"><span className="panel-block-title">Ringkasan Ultimate Beneficial Owner</span><span className="pill info">Efektif &gt; 25%</span></div>
        {ctx.uboReport.length === 0 ? (
          <div className="cell-sub" style={{ padding: "10px 0" }}>Belum ada kepemilikan efektif yang dapat dihitung pada langkah ini.</div>
        ) : (
          <div className="tbl-wrap">
            <table className="data-table" data-testid="ubo-table">
              <thead><tr><th>Individu</th><th>Status</th><th>Kepemilikan Efektif</th></tr></thead>
              <tbody>
                {ctx.uboReport.map((r, i) => (
                  <tr key={i}>
                    <td className="cell-strong">{r.name}</td>
                    <td>{r.isUBO ? <span className="pill bad">Kandidat UBO</span> : <span className="pill neutral">Pemegang</span>}</td>
                    <td>{r.holdings.map((h, j) => <div key={j} className="cell-sub" style={{ fontSize: 12.5, color: h.over25 ? "var(--tx-1)" : undefined, fontWeight: h.over25 ? 600 : 400 }}>{h.company}: {h.pct}</div>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="cell-sub" style={{ marginTop: 12 }}>Hasil kalkulasi asistif untuk membantu peninjauan konsultan, bukan kesimpulan legal final.</div>
      </div>
    </div>
  );
}

/* ================= Pengaturan ================= */
export function SettingsPage(ctx: Ctx) {
  const roleName = ctx.user?.role === "admin" ? "Admin" : ctx.user?.role === "viewer" ? "Viewer" : "Konsultan";
  return (
    <div data-testid="page-pengaturan">
      <div className="page-head"><div className="page-title">Pengaturan</div><div className="page-sub">Profil, tampilan, dan pengaturan akun</div></div>

      <div className="card settings-sec">
        <h3>Profil</h3>
        <div className="ss-sub">Informasi akun Anda.</div>
        <div className="settings-row"><span className="sr-lbl">Nama</span><span className="sr-val">{ctx.user?.name || "—"}</span></div>
        <div className="settings-row"><span className="sr-lbl">Email</span><span className="sr-val">{ctx.user?.email || "—"}</span></div>
        <div className="settings-row"><span className="sr-lbl">Role</span><span className="sr-val"><span className={"pill " + (ctx.user?.role === "admin" ? "bad" : ctx.user?.role === "viewer" ? "neutral" : "info")}>{roleName}</span></span></div>
      </div>

      <div className="card settings-sec">
        <h3>Tampilan</h3>
        <div className="ss-sub">Pilih tema terang atau gelap. Preferensi tersimpan di peramban ini.</div>
        <div className="settings-row">
          <span className="sr-lbl">Tema</span>
          <div className="theme-switch" data-testid="settings-theme">
            <button className={ctx.theme === "light" ? "on" : ""} onClick={() => { if (ctx.theme !== "light") ctx.onToggleTheme(); }} data-testid="theme-light"><Sun size={15} /> Terang</button>
            <button className={ctx.theme === "dark" ? "on" : ""} onClick={() => { if (ctx.theme !== "dark") ctx.onToggleTheme(); }} data-testid="theme-dark"><Moon size={15} /> Gelap</button>
          </div>
        </div>
      </div>

      {ctx.user?.role === "admin" && (
        <div className="card settings-sec">
          <h3>Manajemen Pengguna</h3>
          <div className="ss-sub">Kelola akun konsultan, viewer, dan admin.</div>
          <div className="settings-row"><span className="sr-lbl">Pengguna / Tim</span><button className="d-btn" onClick={ctx.onOpenUsers} data-testid="settings-users"><Users2 size={15} /> Kelola Pengguna</button></div>
        </div>
      )}

      {ctx.user?.role === "admin" && <RetentionSettings />}

      <div className="card settings-sec">
        <h3>Tentang Aplikasi</h3>
        <div className="ss-sub">STRUKTURKU v1.0.0 — by Abadi Premium Consultant.</div>
      </div>
    </div>
  );
}

/* ================= Retensi & Arsip Log Aktivitas (admin) ================= */
function RetentionSettings() {
  const [months, setMonths] = useState<number>(24);
  const [savedMonths, setSavedMonths] = useState<number>(24);
  const [archives, setArchives] = useState<ArchiveItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const [s, a] = await Promise.all([settingsApi.get(), archiveApi.list()]);
      setMonths(s.activity_retention_months); setSavedMonths(s.activity_retention_months);
      setArchives(a.items);
    } catch (e: any) { setErr(formatErr(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveMonths = async () => {
    setBusy(true); setErr(""); setMsg("");
    try { const r = await settingsApi.save(months); setSavedMonths(r.activity_retention_months); setMsg("Kebijakan retensi disimpan."); }
    catch (e: any) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const archiveNow = async () => {
    if (!window.confirm(`Arsipkan semua log yang lebih lama dari ${savedMonths} bulan sekarang? Log yang diarsipkan disimpan sebagai file CSV dan disembunyikan dari daftar aktif (tidak dihapus).`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      // arsipkan yang melewati masa retensi (date_to = batas retensi)
      const cutoff = new Date(Date.now() - savedMonths * 30 * 24 * 3600 * 1000);
      const dto = cutoff.toISOString().slice(0, 10);
      const r = await archiveApi.create("", dto);
      setMsg(`${r.count} entri diarsipkan (${r.period_from} s/d ${r.period_to}).`);
      await load();
    } catch (e: any) {
      const d = e?.detail;
      setErr(typeof d === "string" ? d : formatErr(e));
    }
    finally { setBusy(false); }
  };

  const fmtDT = (iso: string) => { try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

  return (
    <div className="card settings-sec" data-testid="settings-retention">
      <h3>Retensi & Arsip Log Aktivitas</h3>
      <div className="ss-sub">Log lama tetap disimpan permanen — saat diarsipkan, log disalin ke file CSV di penyimpanan lalu disembunyikan dari daftar aktif. Arsip otomatis berjalan tiap malam.</div>
      {err && <div className="auth-err" data-testid="retention-error">{err}</div>}
      {msg && <div className="ok-note" data-testid="retention-msg" style={{ color: "var(--green, #2f7a4c)", fontWeight: 600, margin: "6px 0" }}>{msg}</div>}

      <div className="settings-row">
        <span className="sr-lbl">Masa simpan aktif</span>
        <span className="sr-val" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="number" min={1} max={120} className="filter-select" style={{ width: 90 }} value={months}
            onChange={(e) => setMonths(Math.max(1, Math.min(120, Number(e.target.value) || 1)))} data-testid="retention-months" />
          <span className="cell-sub">bulan</span>
          <button className="d-btn" onClick={saveMonths} disabled={busy || months === savedMonths} data-testid="retention-save"><Save size={14} /> Simpan</button>
        </span>
      </div>

      <div className="settings-row">
        <span className="sr-lbl">Arsip manual</span>
        <span className="sr-val">
          <button className="d-btn" onClick={archiveNow} disabled={busy} data-testid="retention-archive-now"><Archive size={14} /> Arsipkan sekarang</button>
        </span>
      </div>

      <div style={{ marginTop: 14 }}>
        <div className="cell-strong" style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}><Clock size={15} /> Arsip tersimpan ({archives.length})</div>
        {archives.length === 0 ? (
          <div className="cell-sub" data-testid="archives-empty">Belum ada arsip. Arsip dibuat otomatis tiap malam atau lewat tombol "Arsipkan sekarang".</div>
        ) : (
          <div className="tbl-wrap" style={{ border: "1px solid var(--border)", borderRadius: 10 }}>
            <table className="data-table">
              <thead><tr><th>Periode</th><th>Jumlah</th><th>Jenis</th><th>Dibuat</th><th></th></tr></thead>
              <tbody>
                {archives.map((a) => (
                  <tr key={a.id} data-testid={`archive-row-${a.id}`}>
                    <td className="cell-strong">{a.period_from} — {a.period_to}</td>
                    <td>{a.count}</td>
                    <td><span className={"pill " + (a.kind === "auto" ? "info" : "neutral")}>{a.kind === "auto" ? "Otomatis" : "Manual"}</span></td>
                    <td className="cell-sub">{fmtDT(a.created_at)}</td>
                    <td><a className="d-btn ghost" href={archiveApi.downloadUrl(a.id)} data-testid={`archive-download-${a.id}`}><Download size={14} /> Unduh CSV</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatErr(e: any): string {
  const d = e?.detail;
  if (typeof d === "string") return d;
  return "Terjadi kesalahan. Coba lagi.";
}

/* ================= Timeline Perubahan (tab) ================= */
export function ChangeTimelinePage(ctx: Ctx) {
  if (COMPANY_ORDER.length === 0) return <EmptyGroup onExtract={ctx.onOpenExtract} canEdit={ctx.canEdit} />;
  return (
    <div data-testid="page-timeline">
      <div className="page-head">
        <div className="page-title">Timeline Perubahan</div>
        <div className="page-sub">Kronologi perubahan struktur grup <b>{ctx.groupName}</b></div>
      </div>
      <div className="card panel-block">
        {STEPS.length === 0 ? (
          <div className="cell-sub">Belum ada perubahan tercatat.</div>
        ) : (
          STEPS.map((s, i) => (
            <div className="mini-timeline-item" key={i} style={{ padding: "14px 0" }} data-testid={`change-${i}`}>
              <span className="mtl-dot" style={{ background: i === ctx.effStep ? "var(--primary)" : "var(--tx-3)" }} />
              <div style={{ flex: 1 }}>
                <div className="mtl-date">{ddmmyyyy(s.date)} · Langkah {i + 1}</div>
                <div className="mtl-label" style={{ fontWeight: 600 }}>{s.label}</div>
                {s.detail && <div className="cell-sub" style={{ marginTop: 4, lineHeight: 1.5 }}>{s.detail}</div>}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = {
  buat_grup: "Buat grup", buat_grup_ai: "Buat grup (AI)", tambah_pt_ai: "Tambah PT (AI)", susul_dokumen_ai: "Susulkan dokumen (AI)", ubah_nama_grup: "Ubah nama grup",
  hapus_grup: "Hapus grup", upload_dokumen: "Upload dokumen", ekspor: "Ekspor", arsip_log: "Arsip log",
  login: "Masuk", logout: "Keluar", share_buat: "Buat tautan berbagi", share_cabut: "Cabut tautan berbagi",
};
function actionLabel(a: string) { return ACTION_LABELS[a] || a; }
function fmtTime(iso: string) {
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export function AuditLogPage(ctx: Ctx) {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const limit = 25;

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const res = await activityApi.list({ limit, skip: page * limit, action: action || undefined, q: q.trim() || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined });
      setItems(res.items); setTotal(res.total);
    } catch (e: any) { setErr(e?.detail || "Gagal memuat log aktivitas."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action, page, reloadTick]);

  const roleBadge = (r: string) => r === "admin" ? "bad" : r === "viewer" ? "neutral" : "info";
  const roleName = (r: string) => r === "admin" ? "Admin" : r === "viewer" ? "Viewer" : "Konsultan";
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const fetchAll = async () => {
    const base = { action: action || undefined, q: q.trim() || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined };
    const first = await activityApi.list({ limit: 200, skip: 0, ...base });
    let items = first.items.slice();
    const tot = first.total;
    while (items.length < tot) {
      const more = await activityApi.list({ limit: 200, skip: items.length, ...base });
      if (!more.items.length) break;
      items = items.concat(more.items);
    }
    return { items, total: tot };
  };
  const periodLabel = () => {
    if (dateFrom && dateTo) return ` · periode: ${dateFrom} s/d ${dateTo}`;
    if (dateFrom) return ` · sejak: ${dateFrom}`;
    if (dateTo) return ` · sampai: ${dateTo}`;
    return "";
  };
  const filterLabel = () => `${total} entri` + (action ? ` · aksi: ${actionLabel(action)}` : "") + (q.trim() ? ` · cari: "${q.trim()}"` : "") + periodLabel();

  const [exporting, setExporting] = useState(false);
  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await fetchAll();
      const esc = (s: any) => `"${String(s ?? "").replace(/"/g, '""')}"`;
      const header = ["Waktu", "Pengguna", "Email", "Peran", "Aksi", "Tipe Objek", "Objek", "Grup", "IP"];
      const lines = [header.join(",")];
      for (const it of res.items) {
        lines.push([fmtTime(it.created_at), it.user_name, it.user_email, roleName(it.role), actionLabel(it.action), it.target_type, it.target_name, it.group_name, it.ip].map(esc).join(","));
      }
      const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "strukturku-log-aktivitas.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { setErr("Gagal mengekspor CSV."); }
    finally { setExporting(false); }
  };
  const exportPDF = async () => {
    setExporting(true);
    try {
      const res = await fetchAll();
      const rows = res.items.map((it) => ({
        waktu: fmtTime(it.created_at), pengguna: it.user_name || "—", email: it.user_email || "",
        peran: roleName(it.role), aksi: actionLabel(it.action), objek: it.target_name || "—", grup: it.group_name || "—",
      }));
      await exportActivityLogPDF(rows, { filterLabel: filterLabel() });
    } catch { setErr("Gagal mengekspor PDF."); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="page-aktivitas">
      <div className="page-head page-head-row">
        <div>
          <div className="page-title">Log Aktivitas</div>
          <div className="page-sub">Jejak audit: siapa mengubah, mengunggah, atau mengekspor apa &amp; kapan. {total} entri.</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="d-btn" onClick={exportCSV} disabled={exporting || total === 0} data-testid="activity-export-csv"><Download size={15} /> CSV</button>
          <button className="d-btn" onClick={exportPDF} disabled={exporting || total === 0} data-testid="activity-export-pdf"><FileText size={15} /> PDF</button>
          <button className="d-btn" onClick={load} data-testid="activity-refresh"><RefreshCw size={15} /> Segarkan</button>
        </div>
      </div>

      <div className="filter-bar" data-testid="activity-filters">
        <div className="toolbar-search" style={{ maxWidth: 260 }}><Search className="ts-ic" size={15} /><input placeholder="Cari pengguna / objek / grup..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setPage(0); load(); } }} data-testid="activity-search" /></div>
        <label className="filter-field">
          <span>Aksi</span>
          <select className="filter-select" value={action} onChange={(e) => { setPage(0); setAction(e.target.value); }} data-testid="activity-action">
            <option value="">Semua aksi</option>
            {Object.keys(ACTION_LABELS).map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
          </select>
        </label>
        <label className="filter-field">
          <span>Dari tanggal</span>
          <input type="date" className="filter-select" value={dateFrom} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} data-testid="activity-date-from" />
        </label>
        <label className="filter-field">
          <span>Sampai tanggal</span>
          <input type="date" className="filter-select" value={dateTo} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} data-testid="activity-date-to" />
        </label>
        <button className="d-btn ghost" onClick={() => { const n = new Date(); const f = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-01`; const t = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(new Date(n.getFullYear(), n.getMonth() + 1, 0).getDate()).padStart(2, "0")}`; setDateFrom(f); setDateTo(t); setPage(0); setReloadTick((v) => v + 1); }} data-testid="activity-preset-month">Bulan ini</button>
        <button className="d-btn ghost" onClick={() => { const n = new Date(); const qi = Math.floor(n.getMonth() / 3); const sm = qi * 3; const em = sm + 2; const f = `${n.getFullYear()}-${String(sm + 1).padStart(2, "0")}-01`; const t = `${n.getFullYear()}-${String(em + 1).padStart(2, "0")}-${String(new Date(n.getFullYear(), em + 1, 0).getDate()).padStart(2, "0")}`; setDateFrom(f); setDateTo(t); setPage(0); setReloadTick((v) => v + 1); }} data-testid="activity-preset-quarter">Kuartal ini</button>
        <button className="d-btn ghost" onClick={() => { setPage(0); setReloadTick((v) => v + 1); }} data-testid="activity-apply"><Search size={14} /> Terapkan</button>
        {(action || q.trim() || dateFrom || dateTo) && <button className="d-btn ghost" onClick={() => { setAction(""); setQ(""); setDateFrom(""); setDateTo(""); setPage(0); setReloadTick((v) => v + 1); }} data-testid="activity-reset">Reset</button>}
      </div>

      {err && <div className="card" style={{ padding: 16, color: "var(--danger2)" }} data-testid="activity-error">{err}</div>}

      <div className="card tbl-wrap">
        <table className="data-table">
          <thead><tr><th>Waktu</th><th>Pengguna</th><th>Peran</th><th>Aksi</th><th>Objek</th><th>Grup</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6}><div className="cell-sub" style={{ padding: 22, textAlign: "center" }}>Memuat…</div></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6}><div className="cell-sub" style={{ padding: 22, textAlign: "center" }} data-testid="activity-empty">Belum ada aktivitas tercatat.</div></td></tr>
            ) : items.map((it, i) => (
              <tr key={i} data-testid={`activity-row-${i}`}>
                <td className="cell-sub" style={{ whiteSpace: "nowrap" }}>{fmtTime(it.created_at)}</td>
                <td><span className="cell-strong">{it.user_name || "—"}</span><div className="cell-sub">{it.user_email}</div></td>
                <td><span className={"pill " + roleBadge(it.role)}>{roleName(it.role)}</span></td>
                <td><span className="pill info">{actionLabel(it.action)}</span></td>
                <td>{it.target_name || "—"}{it.target_type ? <div className="cell-sub">{it.target_type}</div> : null}</td>
                <td>{it.group_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 14 }}>
          <button className="d-btn ghost" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="activity-prev">Sebelumnya</button>
          <span className="cell-sub">Halaman {page + 1} / {totalPages}</span>
          <button className="d-btn ghost" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="activity-next">Berikutnya</button>
        </div>
      )}
    </div>
  );
}

function EmptyGroup({ onExtract, canEdit }: { onExtract: () => void; canEdit: boolean }) {
  return (
    <div className="card" data-testid="empty-group">
      <div className="empty-state">
        <div className="es-ic"><Building2 size={48} /></div>
        <div className="es-title">Grup ini belum memiliki data</div>
        <div className="es-sub">Unggah dokumen legalitas melalui Ekstraksi AI untuk membentuk struktur kepemilikan grup ini secara otomatis.</div>
        {canEdit && <button className="d-btn-primary" style={{ marginTop: 18 }} onClick={onExtract} data-testid="empty-extract"><Upload size={17} /><span className="pt"><b>Ekstraksi AI</b><small>Upload dokumen untuk memulai</small></span></button>}
      </div>
    </div>
  );
}
